import { Box, Typography, Chip } from "@mui/material";
import { CheckCircle as CheckCircleIcon, RadioButtonUnchecked as RadioButtonUncheckedIcon } from "@mui/icons-material";
import { WIZARD_STEP_TITLES, type WizardStep } from "../WizardContainer.types";

// ============================================================================
// Types
// ============================================================================

export interface StepProgressProps {
	currentStep: WizardStep;
	visibleSteps: WizardStep[];
	completedSteps: Set<WizardStep>;
	resolvedSteps: Set<WizardStep>;
	stepIssueCounts: Partial<Record<WizardStep, number>>;
}

// ============================================================================
// Component
// ============================================================================

export function StepProgress({
	currentStep,
	visibleSteps,
	completedSteps,
	resolvedSteps,
	stepIssueCounts,
}: StepProgressProps) {
	return (
		<Box
			sx={{
				display: "flex",
				alignItems: "center",
				gap: 1,
				flexWrap: "wrap",
				flex: 1,
			}}
		>
			{visibleSteps.map((step, index) => {
				const isActive = step === currentStep;
				const isCompleted = completedSteps.has(step);
				const isResolved = resolvedSteps.has(step);
				const issueCount = stepIssueCounts[step] ?? 0;

				// Show checkmark if step is completed OR resolved
				const showCheckmark = isCompleted || isResolved;

				// Show issue count only if step has issues AND is not showing checkmark
				const showIssueCount = step !== "summary" && issueCount > 0 && !showCheckmark;

				return (
					<Box
						key={step}
						data-testid={`step-${step}`}
						data-active={isActive.toString()}
						data-completed={isCompleted.toString()}
						data-resolved={isResolved.toString()}
						sx={{
							display: "flex",
							alignItems: "center",
							gap: 0.5,
						}}
					>
						{/* Step connector line (except for first step) */}
						{index > 0 && (
							<Box
								sx={{
									width: 24,
									height: 2,
									bgcolor: isCompleted ? "success.main" : "divider",
									mr: 0.5,
								}}
							/>
						)}

						{/* Step icon */}
						{showCheckmark ? (
							<CheckCircleIcon data-testid="check-icon" sx={{ color: "success.main", fontSize: 20 }} />
						) : (
							<RadioButtonUncheckedIcon
								sx={{
									color: isActive ? "primary.main" : "text.disabled",
									fontSize: 20,
								}}
							/>
						)}

						{/* Step label */}
						<Typography
							variant="body2"
							sx={{
								fontWeight: isActive ? 600 : 400,
								color: isActive ? "primary.main" : isCompleted ? "success.main" : "text.secondary",
							}}
						>
							{WIZARD_STEP_TITLES[step]}
						</Typography>

						{/* Issue count badge */}
						{showIssueCount && (
							<Chip
								data-testid="issue-count-badge"
								label={issueCount}
								size="small"
								color={isActive ? "primary" : "default"}
								sx={{
									height: 20,
									fontSize: "0.75rem",
									ml: 0.5,
								}}
							/>
						)}
					</Box>
				);
			})}
		</Box>
	);
}
